import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  ClickAwayListener,
  InputAdornment,
  Popper,
  Grid,
  Button,
  Paper,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { NumberRange } from '../NumberRange/NumberRange';
import { Concatenate } from 'utils/string/string';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { DisableAppScroll, EnableAppScroll } from 'app/layout/Layout';
import { useFormContext, useWatch } from 'react-hook-form';

const useStyles = makeStyles()(theme => ({
  textField: {
    fontSize: 12,
    '& input::placeholder': {
      fontSize: 12,
    },
  },
  headerContainer: {
    padding: '12px 20px 6px',
  },
  popper: {
    marginTop: 4,
    zIndex: 5000,
  },
  paperDropdown: {
    boxShadow: '0px 2px 8px #0000004D',
  },
  button: {
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'none',
    '&:hover, &:focus': {
      background: 'none',
    },
    opacity: 1,
  },
  actionContainer: {
    backgroundColor: '#ffffff',
    borderTop: '1px solid #cccccc',
    padding: '0px 8px',
    borderBottomLeftRadius: 4,
    borderBottomRightRadius: 4,
  },
  boxContainer: {
    backgroundColor: '#ffffff',
    padding: '0px 20px',
    height: 70,
  },

  inActive: {
    color: '#888888',
  },
  clearAllButton: {
    padding: 0,
  },
  actionButton: {
    padding: 12,
  },
}));

export const getNumberRange = value =>
  value === null
    ? ''
    : Concatenate(
        [value.min === null ? null : value.min.toString(), value.max === null ? null : value.max.toString()],
        ' - ',
      );

const rangesEqual = (a: any, b: any) => {
  if (a === b) return true;
  if (a === null || a === undefined || b === null || b === undefined) return a === b;
  return a.min === b.min && a.max === b.max;
};

export const CustomNumberRange = (props: any) => {
  const {
    supportsBlankNumberRangeFilter,
    blankFieldName,
    blankCheckboxLabel,
    value,
    onChange,
    apply,
    applyOnClickAway,
    filterName,
    placeholder,
    numberRangeType,
  } = props;

  const { classes } = useStyles();
  const textRef = useRef(null);
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [prevState, setPrevState] = useState<any>(value);
  const [prevBlankState, setPrevBlankState] = useState(false);
  const { control, setValue } = useFormContext();

  const blankEnabled = !!(supportsBlankNumberRangeFilter && blankFieldName);
  const blankFormValue = !!useWatch({
    control,
    name: blankFieldName || '__customNumberRangeNoBlank',
    defaultValue: false,
    disabled: !blankEnabled,
  });

  const [pendingBlankChecked, setPendingBlankChecked] = useState(blankFormValue);

  useEffect(() => {
    setPendingBlankChecked(blankFormValue);
  }, [blankFormValue]);

  const open = Boolean(anchorEl);

  const rangeHasLimits = (v: any) => !!(v && (v.min != null || v.max != null));

  const hasCommittedSelection = !!(rangeHasLimits(value) || (blankEnabled && blankFormValue));

  const canClear = rangeHasLimits(value) || (blankEnabled && (pendingBlankChecked || blankFormValue));

  // Calculate dynamic width based on filter name length
  const calculateWidth = () => {
    // Dynamically measure the label text and add padding/icon room
    const fontSpec = '12px Roboto, Helvetica, Arial, sans-serif';
    const measureTextWidth = (text: string) => {
      if (!text) return 0;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return text.length * 7.5; // fallback approximation
      ctx.font = fontSpec;
      return ctx.measureText(text).width;
    };

    const labelTextBase = placeholder || filterName || '';
    const labelText = hasCommittedSelection ? `(1) ${labelTextBase}` : labelTextBase;
    const textWidth = measureTextWidth(labelText);

    // Account for input paddings, outline, and end icon chevron
    const paddingAndIcon = 64; // ~left/right padding + chevron space
    const safety = 8; // small buffer to avoid edge clipping
    const minWidth = 140; // reasonable minimum
    const maxWidth = 340; // prevent overly wide controls

    const computed = Math.ceil(textWidth + paddingAndIcon + safety);
    return Math.max(minWidth, Math.min(maxWidth, computed));
  };

  const onClickAway = e => {
    if (e.target.type !== 'submit') {
      const rangeDirty = !rangesEqual(prevState, value);
      const blankDirty = blankEnabled && pendingBlankChecked !== prevBlankState;
      if (applyOnClickAway && (rangeDirty || blankDirty)) {
        onApplyEvent();
      } else {
        cancelChanges();
      }
    }
  };

  const clearAll = () => {
    if (blankEnabled && blankFieldName) {
      setPendingBlankChecked(false);
      setValue(blankFieldName, false, { shouldDirty: true });
    }
    onChange(null);
  };

  const closeActions = () => {
    setAnchorEl(null);
    EnableAppScroll();
  };

  const cancelChanges = () => {
    onChange(prevState);
    if (blankEnabled && blankFieldName) {
      setValue(blankFieldName, prevBlankState, { shouldDirty: true });
      setPendingBlankChecked(prevBlankState);
    }
    closeActions();
  };

  const onApplyEvent = () => {
    if (blankEnabled && blankFieldName) {
      if (pendingBlankChecked) {
        setValue(blankFieldName, true, { shouldDirty: true });
        onChange(null);
        apply(null);
      } else {
        setValue(blankFieldName, false, { shouldDirty: true });
        apply(value);
      }
    } else {
      apply(value);
    }
    closeActions();
  };

  const handleOpen = event => {
    if (!open) {
      setAnchorEl(event.currentTarget);
      setPrevState(value);
      if (blankEnabled) {
        setPrevBlankState(blankFormValue);
        setPendingBlankChecked(blankFormValue);
      }
      DisableAppScroll();
    } else cancelChanges();
  };

  const handleRangeChange = newRange => {
    if (
      blankEnabled &&
      blankFieldName &&
      newRange &&
      (newRange.min !== null || newRange.max !== null)
    ) {
      setPendingBlankChecked(false);
      setValue(blankFieldName, false, { shouldDirty: true });
    }
    onChange(newRange);
  };

  const blankFooter = blankEnabled ? (
    <FormControlLabel
      sx={{
        margin: 0,
        mr: 'auto',
        '& .MuiFormControlLabel-label': { fontSize: 12 },
      }}
      control={
        <Checkbox
          size="small"
          checked={pendingBlankChecked}
          onChange={e => {
            const next = e.target.checked;
            setPendingBlankChecked(next);
            if (next) {
              onChange(null);
            }
          }}
        />
      }
      label={blankCheckboxLabel ?? 'Blank'}
    />
  ) : null;

  return (
    <Box>
      <TextField
        inputRef={textRef}
        value={hasCommittedSelection ? `(1) ${filterName}` : ''}
        placeholder={placeholder || ''}
        variant="outlined"
        size="small"
        classes={{ root: classes.textField }}
        inputProps={{ 'aria-label': 'number-text-field' }}
        onClick={handleOpen}
        style={{ width: calculateWidth() }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <KeyboardArrowDownIcon style={{ fontSize: 20, color: '#c1c1c1' }} />
            </InputAdornment>
          ),
          style: {
            color: hasCommittedSelection ? '#555555' : undefined,
            borderRadius: 4,
            width: '100%',
            minHeight: '36.7px',
            justifyContent: 'space-between',
            textTransform: 'none',
            fontSize: 12,
            fontWeight: 400,
          },
          // classes: { underline: classes.underline, focused: classes.focused },
        }}
      />
      <Popper open={open} className={classes.popper} anchorEl={anchorEl} placement={'bottom-start'}>
        <ClickAwayListener onClickAway={onClickAway}>
          <Paper classes={{ root: classes.paperDropdown }}>
            <Grid
              item
              container
              justifyContent={'flex-end'}
              alignItems="center"
              classes={{ root: classes.headerContainer }}
            >
              <Button
                aria-label="clear-all-button"
                variant="text"
                color="primary"
                disableRipple
                disableFocusRipple
                disableTouchRipple
                disabled={!canClear}
                onClick={() => clearAll()}
                classes={{ text: ` ${classes.button} ${classes.clearAllButton}` }}
              >
                {t('search.filter.clearAll')}
              </Button>
            </Grid>
            <div aria-label="custom-number-range" className={classes.boxContainer}>
              <NumberRange onChange={handleRangeChange} range={value} numberRangeType={numberRangeType} />
            </div>
            <div className={classes.actionContainer}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'nowrap' }}>
                {blankFooter}
                <Box sx={{ display: 'flex', alignItems: 'center', ml: blankFooter ? 1 : 'auto' }}>
                  <Button
                    aria-label="cancel-button"
                    variant="text"
                    disableRipple
                    disableFocusRipple
                    disableTouchRipple
                    onClick={() => cancelChanges()}
                    classes={{ text: `${classes.button} ${classes.actionButton} ${classes.inActive}` }}
                  >
                    {t('search.filter.cancel')}
                  </Button>
                  <Button
                    aria-label="apply-button"
                    variant="text"
                    color="primary"
                    disableRipple
                    disableFocusRipple
                    disableTouchRipple
                    onClick={() => onApplyEvent()}
                    classes={{ text: `${classes.button} ${classes.actionButton}` }}
                  >
                    {t('search.filter.apply')}
                  </Button>
                </Box>
              </Box>
            </div>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};
